import * as React from 'react'

import { cn } from '../../lib/utils'

const MAX_WIDTH = {
  '2xl': 'max-w-2xl',
  '4xl': 'max-w-4xl',
  '5xl': 'max-w-5xl',
  '6xl': 'max-w-6xl',
  // For hosts whose own shell already caps and centres the column — the booking
  // app's `BookingPageShell` (max-w-6xl) plus `BookingPanelLayout` (640px main)
  // do — so this doesn't add a second cap inside them.
  none: '',
} as const

export interface PageProps {
  children: React.ReactNode
  /**
   * Centred content cap. `4xl` is THE app-wide default for every page
   * type — lists, tables, forms and dashboards alike (uniform width was
   * chosen over per-content caps, July 2026). Only specify a value for a
   * deliberate, justified exception.
   */
  maxWidth?: keyof typeof MAX_WIDTH
  /**
   * Full-bleed the body on mobile — the scroller drops its horizontal gutter
   * (`px-0`) so a `<Table>` goes edge-to-edge (and drops its side border via
   * `@max-2xl`). **Off by default** (padded, safe for forms + focusable
   * controls). Turn on for table/list pages that should reach the screen edge
   * on a phone. Desktop is always padded + centred regardless.
   *
   * ⚠️ It strips the gutter for EVERY child, not just the table. A bordered
   * card / callout / banner sitting above the table (a hardware nudge, a
   * pending-invitations list, a `StatusCallout`) then jams against both screen
   * edges on a phone. Give those siblings their own `px-4 @2xl:px-0` — and put
   * it INSIDE the component (merged onto its root, or a wrapper it renders only
   * when it has something to show) rather than in a wrapper div here: a
   * conditional child that renders `null` still leaves a ghost `gap` in the
   * page's flex column if its wrapper survives.
   */
  bleed?: boolean
  /**
   * Toolbar pinned above the scroll area (search + actions). The row spans the
   * full width (padding), but its content AND its bottom hairline are capped to
   * the same centred column as the body, so the divider and the actions line up
   * with the content beneath. The hairline shows from the `sm` VIEWPORT up —
   * on a phone the toolbar reads cleaner with no line under it. (Viewport, not
   * container: a narrow column on desktop still gets its line.)
   */
  toolbar?: React.ReactNode
  /**
   * Left-hand toolbar slot (a back button, a title). When present the row
   * switches to `justify-between`: this renders on the left, `toolbar` on the
   * right. Omit it and the row stays a single right-aligned cluster, so no
   * existing page shifts.
   */
  toolbarStart?: React.ReactNode
  /**
   * Full-width footer pinned below the scroll area.
   *
   * ⚠️ **Don't reach for this.** We no longer put sticky footer bars on pages:
   * a pinned strip spends permanent vertical space on something the guest reads
   * once, and it competes with the toolbar's action for the eye. Put the content
   * at the end of the scroll instead (the checkout step counter does exactly
   * that, dashed rule and all). A `<Table>` also owns its own pagination footer,
   * so it doesn't need this either.
   *
   * Kept for the one page that still uses it
   * (`connect/.../bookings/csv-imports/new.tsx`); no new callers.
   */
  footer?: React.ReactNode
  /**
   * Page / step heading, rendered as the FIRST thing in the body (not in the
   * toolbar). The toolbar is left to navigation + actions, so a heading beside
   * a back button that comes and goes between steps can't shift sideways.
   */
  title?: React.ReactNode
  /**
   * Plain-text context under `title` — pass a `BookingMetaLine`. Title and meta
   * are one tight cluster (`gap-1`), held off the content by the body's rhythm.
   */
  meta?: React.ReactNode
  /**
   * Whether the root claims its parent's full height (`h-full`). **On by
   * default**, which is what you want whenever the parent has a definite height:
   * connect's bounded app shell, or the booking shell with `fillContent` +
   * `BookingPanelLayout fill`. The page then fills it and its body is the only
   * scroller.
   *
   * Turn it OFF only where the page must size to its content and let an ancestor
   * scroll instead. Then the root gets NO height and NO `flex-1`, and the caller
   * supplies whatever it needs via `className`.
   *
   * Either way: a `Page` whose height is indefinite simply grows, and then
   * nothing scrolls under its toolbar.
   */
  fill?: boolean
  /**
   * Which canvas the under-toolbar fade dissolves into. `background` (default)
   * is connect's page canvas. Booking pages sit on the page-shell's INNER
   * canvas, which is `card` (see `booking-panel-layout`) — fading from
   * `background` there reads as a lighter band across the top of the content.
   */
  fadeFrom?: 'background' | 'card'
  /**
   * Bottom clearance on mobile so the last content scrolls clear of connect's
   * floating Menu pill. **On by default** (every connect page wants it). Turn
   * it off in hosts with no such pill — in the booking app it is just 72px of
   * dead space above the footer on a phone.
   */
  mobileBottomClearance?: boolean
  /** Extra classes on the root (e.g. the page's `new-ui-theme bg-background`). */
  className?: string
  /** Extra classes on the centred content wrapper. */
  contentClassName?: string
}

/**
 * Page — THE page scaffold, for connect and for the guest-facing booking flow
 * alike. ONE shell for every standard page (list, table, form, detail,
 * dashboard, checkout step, confirmation); they differ only by props. Sits
 * INSIDE the app scroll area (unlike `PageShell`, which is a standalone `h-svh`
 * canvas). It owns every page-level layout decision so no page re-derives them
 * and none drift — consistency by construction:
 *
 *   • `@container` root `flex h-full min-h-0 flex-col` — fills the parent, no
 *     own height; every responsive gutter below keys off THIS width (the real
 *     content width, not the viewport, which lies behind the sidebar)
 *   • optional `toolbar` — content + bottom hairline capped to the centred
 *     column so the divider + search / actions align with the content beneath
 *     (one tidy centred stack, not a right-pinned bar)
 *   • a scroller: **padded + centred** (`p-4` → `@2xl:p-6`, `mx-auto max-w-*`),
 *     with `scroll-py-4` so focus rings don't clip. `bleed` drops the mobile
 *     horizontal gutter so tables reach the screen edge on a phone.
 *   • optional `footer` — top hairline + content capped to the column
 *
 * Assumes the v4 tokens resolve — wrap the page root in `new-ui-theme` (or pass
 * it via `className`). Rhythm WITHIN the content is the caller's: stack
 * sections with `flex flex-col gap-*`, never `space-y`/margins (design law §1).
 *
 * The toolbar is a `shrink-0` SIBLING above the scroller, never sticky and
 * never a floating bar. That is what puts a page's action at the top at every
 * width, with no per-breakpoint variant and nothing that can scroll away — so
 * don't reintroduce a fixed bottom action bar on mobile.
 *
 * In the BOOKING app pass `maxWidth="none" fadeFrom="card"
 * mobileBottomClearance={false}`: its shell already caps + centres the column,
 * its inner canvas is `card`, and it has no floating nav pill. Height comes from
 * the shell (`fillContent` + `BookingPanelLayout fill`), so leave `fill` alone.
 */
export function Page({
  children,
  maxWidth = '4xl',
  bleed = false,
  toolbar,
  toolbarStart,
  title,
  meta,
  footer,
  fill = true,
  fadeFrom = 'background',
  mobileBottomClearance = true,
  className,
  contentClassName,
}: PageProps) {
  const contentCap = cn('mx-auto w-full', MAX_WIDTH[maxWidth])
  const scrollerGutter = bleed ? 'px-0 py-4 @2xl:p-6' : 'p-4 @2xl:p-6'

  return (
    <div
      data-slot="page"
      // `@container` on the root so the toolbar / scroller / footer all key
      // their responsive gutter off the real content width (not the viewport,
      // which lies behind the sidebar) — and a `<Table>`'s `@max-2xl` full-bleed
      // fires on that same width.
      className={cn(
        '@container flex min-h-0 flex-col',
        // `h-full` fills a parent with a definite height. With `fill={false}` the
        // root gets NO height and NO `flex-1`; the caller supplies what it needs.
        //
        // Deliberately not `flex-1` here. `flex-1` is `flex: 1 1 0%`, and
        // flex-basis takes precedence over `height` for a flex item's main size,
        // so it would silently cancel the caller's height, leave the page
        // content-sized, and the body's `overflow-auto` would then have nothing
        // to scroll against. Symptom: the toolbar scrolls away with the content
        // and nothing passes underneath it.
        fill && 'h-full',
        className
      )}
    >
      {/* Default: a single right-aligned cluster (`justify-end`) — search +
          actions sit at the right of the centred column. With `toolbarStart`
          (a back button, a title) the row becomes `justify-between`: start
          cluster left, actions right. */}
      {toolbar || toolbarStart ? (
        <div
          data-slot="page-toolbar"
          className="relative z-10 px-4 pt-4 @2xl:px-6"
        >
          {/* Hairline capped to the content column (a bordered inner block)
              so the divider lines up with the table/content edges rather than
              running full-width past them. `pb-4` keeps the original rhythm. */}
          <div
            className={cn(
              // Divider hidden on PHONES only — `sm:` viewport, deliberately
              // NOT a container query like the rest of this block. "No line"
              // is a phone rule, not a narrow-column rule: keyed to `@2xl`
              // (672px) it also vanished on desktop wherever a host caps the
              // column below that (booking's main column is 640px, so its
              // desktop toolbar lost the line entirely).
              //
              // `pb-2` at every width, NOT just `sm:pb-4`: a toolbar control's
              // focus ring is drawn ~3px OUTSIDE its box, and the under-toolbar
              // fade below is `absolute top-full` starting opaque, so with zero
              // bottom padding it painted straight over the bottom of a focused
              // search input's ring (looked clipped on phones only, where
              // `sm:pb-4` is off). The ring needs clearance inside the toolbar's
              // own box.
              'flex flex-wrap items-center gap-2 pb-2 sm:border-b sm:border-dashed sm:border-border sm:pb-4',
              toolbarStart ? 'justify-between' : 'justify-end',
              contentCap
            )}
          >
            {/*
              THE GROUPS WRAP TOO, not just the row that holds them.

              The row outside is `flex-wrap`, so a toolbar too wide for its
              line moves to the next one WHOLE. That is no help when the
              group itself is wider than the page: it moved down and ran
              off the right edge just the same, and the last control (which
              is the primary one, because it sits last) was the half that
              disappeared. Ordering > Products does it with five: a Square
              account name, a sold-at toggle, two Square actions and Add.

              `min-w-0` with it, because a flex item will not shrink below
              its content without it and the wrap never gets a chance.
            */}
            {toolbarStart ? (
              <div className="flex min-w-0 flex-wrap items-center gap-2">
                {toolbarStart}
              </div>
            ) : null}
            {toolbarStart ? (
              <div className="flex min-w-0 flex-wrap items-center justify-end gap-2">
                {toolbar}
              </div>
            ) : (
              toolbar
            )}
          </div>
          {/* A slight fade under the toolbar so scrolling content dissolves
              softly into it as it passes beneath — like the mobile nav's
              bottom fade, but a fraction of the height. */}
          <div
            aria-hidden
            className={cn(
              'pointer-events-none absolute inset-x-0 top-full h-8 bg-gradient-to-b to-transparent',
              fadeFrom === 'card' ? 'from-card' : 'from-background'
            )}
          />
        </div>
      ) : null}

      <div
        data-slot="page-scroll"
        className={cn(
          'min-h-0 flex-1 overflow-auto scroll-py-4',
          scrollerGutter
        )}
      >
        {/* Heading cluster: capped to the same column, but its OWN block rather
            than a child of the content wrapper — the wrapper's rhythm belongs to
            the caller (it often sets `contentClassName="flex flex-col gap-*"`),
            and a heading inside it would pick that gap up on top of its own
            spacing. `gap-1` binds title to its meta; `pb-5` holds the pair off
            the content. */}
        {title || meta ? (
          <div className={cn(contentCap, 'flex flex-col gap-1 pb-5')}>
            {title ? (
              <p className="font-heading text-base font-semibold">{title}</p>
            ) : null}
            {meta}
          </div>
        ) : null}
        <div
          className={cn(
            contentCap,
            // Mobile only: extra room below the page's last content so it
            // scrolls clear of the app's floating Menu pill instead of
            // hiding behind it. Lives on the PAGE's own content (not the
            // app layout shell — that would gap out full-height surfaces
            // like the day planner, which isn't a Page). `max-md` matches
            // the pill's `md:hidden`; no effect on desktop. Hosts without
            // that pill opt out via `mobileBottomClearance={false}`.
            mobileBottomClearance &&
              'max-md:pb-[calc(env(safe-area-inset-bottom)_+_4.5rem)]',
            contentClassName
          )}
        >
          {children}
        </div>
      </div>

      {footer ? (
        <div data-slot="page-footer" className="px-4 pb-4 @2xl:px-6">
          {/* Top hairline capped to the content column, matching the toolbar. */}
          <div
            className={cn(
              'border-t border-dashed border-border pt-4',
              contentCap
            )}
          >
            {footer}
          </div>
        </div>
      ) : null}
    </div>
  )
}
